import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, of, switchMap, tap } from 'rxjs';
import {
  buildStockHistoryDisplayItems,
  StockHistoryGroupDisplayItem,
  StockHistoryRow,
} from './stock-history-grouping';
import { applySearchRule } from '../../../../@core/utils/search-input-helper';
import { GET_STOCK_TRANSACTIONS_WITH_USER } from '../../../../data/definitions/warehouse-definition';
import {
  Employee,
  GetStockTransactionsWithUserQuery,
  Operator,
  StockAction,
  StockTransactionSearchInput,
} from '../../../../generated/generated-types';
import { PhxAvatarComponent } from '../../../../shared/components/phx-avatar/phx-avatar.component';
import { LocaleCurrencyPipe } from '../../../../shared/pipes/locale-currency.pipe';
import { LocaleDatePipe } from '../../../../shared/pipes/locale-date.pipe';
import { StockTransactionService } from '../../services/stock-transaction.service';

type StoragePlaceRow = StockHistoryRow['originStoragePlace'];

@Component({
  selector: 'app-stock-history-cards',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    LocaleCurrencyPipe,
    PhxAvatarComponent,
    TooltipModule,
  ],
  templateUrl: './stock-history-cards.component.html',
  host: {
    class: 'flex min-h-0 w-full min-w-0 flex-1 flex-col',
  },
})
export class StockHistoryCardsComponent {
  private readonly stockTransactionService = inject(StockTransactionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly localeDatePipe = inject(LocaleDatePipe);

  /** Document line item id to filter stock transactions. */
  readonly dliId = input<string | undefined>(undefined);

  /** Document definition stock action (for reserve-specific empty state). */
  readonly stockAction = input<StockAction | undefined>(undefined);

  /** Document ISO currency for unit value formatting. */
  readonly currencyCode = input<string | null | undefined>(undefined);

  /** When true, show unit value inline with quantity in the card header. */
  readonly showTransactionValue = input<boolean>(false);

  /** Fallback unit (e.g. from the open document line item) when the transaction has no unit. */
  readonly defaultUnitIdentifier = input<string | null | undefined>(undefined);

  readonly loading = signal(true);
  readonly loadFailed = signal(false);
  readonly rawItems = signal<StockHistoryRow[]>([]);
  readonly sortNewestFirst = signal(true);

  readonly sortedItems = computed(() =>
    this.sortTransactions(this.rawItems(), this.sortNewestFirst())
  );

  readonly displayItems = computed(() => buildStockHistoryDisplayItems(this.sortedItems()));

  private readonly expandedGroupKeys = signal<ReadonlySet<string>>(new Set());

  readonly isEmpty = computed(
    () => !this.loading() && !this.loadFailed() && this.rawItems().length === 0
  );

  readonly transactionCount = computed(() => this.rawItems().length);

  readonly showSortControl = computed(
    () => !this.loading() && !this.loadFailed() && this.rawItems().length > 1
  );

  readonly sortIcon = computed(() =>
    this.sortNewestFirst()
      ? 'fa-solid fa-arrow-down-wide-short'
      : 'fa-solid fa-arrow-up-wide-short'
  );

  readonly sortTooltipKey = computed(() =>
    this.sortNewestFirst()
      ? 'document-line-item.stock-history.sort-newest-first'
      : 'document-line-item.stock-history.sort-oldest-first'
  );

  readonly emptyMessageKey = computed(() =>
    this.stockAction() === StockAction.RESERVE
      ? 'document-line-item.stock-history-empty-reserve'
      : 'document-line-item.stock-history-empty'
  );

  constructor() {
    toObservable(this.dliId)
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.loadFailed.set(false);
          this.sortNewestFirst.set(true);
          this.expandedGroupKeys.set(new Set());
        }),
        switchMap((id) => {
          if (!id) {
            return of([] as StockHistoryRow[]);
          }
          const searchInput: StockTransactionSearchInput = {
            take: 200,
            skip: 0,
            searchFilter: { rules: [] },
          };
          applySearchRule(searchInput, {
            field: 'documentLineItemId',
            operator: Operator.equal,
            value: id,
          });
          return this.stockTransactionService
            .getItems(searchInput, { fragment: GET_STOCK_TRANSACTIONS_WITH_USER })
            .mapSingle(
              (data: GetStockTransactionsWithUserQuery) =>
                data.getStockTransactions?.items ?? []
            )
            .pipe(
              catchError((error: unknown) => {
                this.loadFailed.set(true);
                console.error(
                  `Failed to load stock transactions for documentLineItemId: ${id}`,
                  error
                );
                return of([] as StockHistoryRow[]);
              })
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((items) => {
        this.rawItems.set(items);
        this.loading.set(false);
      });
  }

  toggleSortByTransactionDate(): void {
    this.sortNewestFirst.update((current) => !current);
  }

  private sortTransactions(items: StockHistoryRow[], newestFirst: boolean): StockHistoryRow[] {
    const direction = newestFirst ? -1 : 1;
    return [...items].sort((a, b) => {
      const dateDiff =
        direction *
        (this.transactionSortTime(a.dateOfTransaction) -
          this.transactionSortTime(b.dateOfTransaction));
      if (dateDiff !== 0) {
        return dateDiff;
      }
      return (
        direction *
        (this.transactionSortTime(a.createdAt) - this.transactionSortTime(b.createdAt))
      );
    });
  }

  private transactionSortTime(value: string | Date | null | undefined): number {
    if (value == null || value === '') {
      return 0;
    }
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  hasOriginPlace(tx: StockHistoryRow): boolean {
    return !!(tx.originStoragePlaceId ?? tx.originStoragePlace);
  }

  hasTargetPlace(tx: StockHistoryRow): boolean {
    return !!(tx.targetStoragePlaceId ?? tx.targetStoragePlace);
  }

  isRelocate(tx: StockHistoryRow): boolean {
    return this.hasOriginPlace(tx) && this.hasTargetPlace(tx);
  }

  isOutbound(tx: StockHistoryRow): boolean {
    return this.hasOriginPlace(tx) && !this.hasTargetPlace(tx);
  }

  isInbound(tx: StockHistoryRow): boolean {
    return !this.hasOriginPlace(tx) && this.hasTargetPlace(tx);
  }

  showStorageRoute(tx: StockHistoryRow): boolean {
    return this.hasOriginPlace(tx) || this.hasTargetPlace(tx);
  }

  notAvailable(): string {
    return this.translate.instant('common.no-a');
  }

  displayText(value: string | null | undefined): string {
    const trimmed = value?.trim();
    return trimmed || this.notAvailable();
  }

  productDisplay(tx: StockHistoryRow): string {
    const description = tx.product?.description?.trim();
    const identifier = tx.product?.identifier?.trim();
    return this.displayText(description ?? identifier ?? undefined);
  }

  transactionEmployee(tx: StockHistoryRow): Employee | null {
    return tx.createdBy?.activeEmployee ?? null;
  }

  hasTransactionEmployee(tx: StockHistoryRow): boolean {
    return this.transactionEmployee(tx) != null;
  }

  actorDisplay(tx: StockHistoryRow): string {
    const userId = tx.createdBy?.identifier?.trim();
    const employee = tx.createdBy?.activeEmployee;
    const employeeName = employee
      ? [employee.name?.trim(), employee.lastName?.trim()].filter(Boolean).join(' ')
      : '';
    if (userId && employeeName) {
      return `${userId} (${employeeName})`;
    }
    if (userId) {
      return userId;
    }
    const actor = tx.actor?.trim();
    if (actor) {
      return actor;
    }
    return this.notAvailable();
  }

  storagePlaceSummary(place: StoragePlaceRow | null | undefined): string {
    const id = place?.identifier?.trim();
    const desc = place?.description?.trim();
    if (id && desc) {
      return `${id} — ${desc}`;
    }
    return this.displayText(id ?? desc ?? undefined);
  }

  storageRouteLine(tx: StockHistoryRow): string {
    if (this.isRelocate(tx)) {
      return `${this.storagePlaceSummary(tx.originStoragePlace)} → ${this.storagePlaceSummary(tx.targetStoragePlace)}`;
    }
    if (this.isOutbound(tx)) {
      return this.storagePlaceSummary(tx.originStoragePlace);
    }
    if (this.isInbound(tx)) {
      return this.storagePlaceSummary(tx.targetStoragePlace);
    }
    return this.notAvailable();
  }

  storageRouteIcon(tx: StockHistoryRow): string {
    if (this.isRelocate(tx)) {
      return 'fa-solid fa-arrows-turn-to-dots text-xs';
    }
    if (this.isInbound(tx)) {
      return 'fa-solid fa-arrow-trend-up text-xs';
    }
    if (this.isOutbound(tx)) {
      return 'fa-solid fa-arrow-trend-down text-xs';
    }
    return 'fa-solid fa-location-dot text-xs';
  }

  storageRouteIconClasses(tx: StockHistoryRow): string {
    if (this.isRelocate(tx)) {
      return 'bg-primary/10 text-primary';
    }
    if (this.isInbound(tx)) {
      return 'bg-green-100 text-green-600 dark:bg-green-950/50 dark:text-green-400';
    }
    if (this.isOutbound(tx)) {
      return 'bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-400';
    }
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  }

  quantityNumber(tx: StockHistoryRow): string {
    if (tx.quantity == null) {
      return this.notAvailable();
    }
    return String(tx.quantity);
  }

  quantityUnit(tx: StockHistoryRow): string | null {
    const fromTransaction = tx.unit?.identifier?.trim();
    if (fromTransaction) {
      return fromTransaction;
    }
    const fromProduct = tx.product?.unit?.identifier?.trim();
    if (fromProduct) {
      return fromProduct;
    }
    const fallback = this.defaultUnitIdentifier()?.trim();
    if (fallback) {
      return fallback;
    }
    return null;
  }

  formatExpiryInline(tx: StockHistoryRow): string {
    return this.formatExpiryDate(tx.expirationDate, 'shortDate');
  }

  formatExpiryLong(tx: StockHistoryRow): string {
    return this.formatExpiryDate(tx.expirationDate, 'longDate');
  }

  /** Product article number for the subline when the title already shows the description. */
  productIdentifierInline(tx: StockHistoryRow): string | null {
    const identifier = tx.product?.identifier?.trim();
    const description = tx.product?.description?.trim();
    if (!identifier || !description) {
      return null;
    }
    return identifier;
  }

  /** Lot and serial as a single compact line (lot before serial), or null if neither is set. */
  identificationInline(tx: StockHistoryRow): string | null {
    const parts: string[] = [];
    const lot = tx.lotNumber?.trim();
    const serial = tx.serialNumber?.trim();
    if (lot) {
      parts.push(lot);
    }
    if (serial) {
      parts.push(serial);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }

  private formatExpiryDate(
    value: string | Date | null | undefined,
    format: 'shortDate' | 'longDate'
  ): string {
    if (value == null || value === '') {
      return this.notAvailable();
    }
    return this.localeDatePipe.transform(value, format) ?? this.notAvailable();
  }

  formatTransactionTime(tx: StockHistoryRow): string {
    return this.formatTransactionTimeValue(tx.createdAt);
  }

  private formatTransactionTimeValue(value: string | Date | null | undefined): string {
    if (value == null || value === '') {
      return this.notAvailable();
    }
    return this.localeDatePipe.transform(value, 'short') ?? this.notAvailable();
  }

  hasNotes(tx: StockHistoryRow): boolean {
    return !!tx.notes?.trim();
  }

  showCardValue(tx: StockHistoryRow): boolean {
    return this.showTransactionValue() && tx.value != null;
  }

  isGroupExpanded(trackId: string): boolean {
    return this.expandedGroupKeys().has(trackId);
  }

  toggleGroupExpanded(trackId: string): void {
    this.expandedGroupKeys.update((keys) => {
      const next = new Set(keys);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  }

  groupTotalQuantity(group: StockHistoryGroupDisplayItem): number {
    return group.transactions.reduce((sum, tx) => sum + (tx.quantity ?? 0), 0);
  }

  groupQuantityUnit(group: StockHistoryGroupDisplayItem): string | null {
    return this.quantityUnit(group.representativeTx);
  }

  groupToggleIcon(trackId: string): string {
    return this.isGroupExpanded(trackId)
      ? 'fa-solid fa-chevron-up'
      : 'fa-solid fa-chevron-down';
  }
}
